import { useRef } from "react";

/**
 * MagneticGlassBox — an interactive glassmorphic container that reacts
 * to mouse movement with 3D tilt, a spotlight that follows the cursor,
 * and a tracking border-shine. Designed to sit on dark backgrounds.
 */
export default function MagneticGlassBox({
  children,
  className = "",
  max = 12,
  scale = 1.03,
  spotlightColor = "rgba(125,211,252,0.28)",
  ...rest
}) {
  const ref = useRef(null);
  const spotRef = useRef(null);
  const borderRef = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width;
    const my = (e.clientY - rect.top) / rect.height;
    const rx = ((my - 0.5) * -max).toFixed(2);
    const ry = ((mx - 0.5) * max).toFixed(2);
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) scale(${scale})`;
    if (spotRef.current) {
      spotRef.current.style.background = `radial-gradient(420px circle at ${mx * 100}% ${my * 100}%, ${spotlightColor}, transparent 45%)`;
      spotRef.current.style.opacity = "1";
    }
    if (borderRef.current) {
      borderRef.current.style.background = `radial-gradient(200px circle at ${mx * 100}% ${my * 100}%, rgba(255,255,255,0.55), transparent 55%)`;
      borderRef.current.style.opacity = "1";
    }
  };

  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = `perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)`;
    if (spotRef.current) spotRef.current.style.opacity = "0";
    if (borderRef.current) borderRef.current.style.opacity = "0";
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={`relative will-change-transform transition-transform duration-200 ease-out ${className}`}
      style={{ transformStyle: "preserve-3d" }}
      {...rest}
    >
      {children}
      {/* spotlight layer (inside, clipped by container) */}
      <div
        ref={spotRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300 mix-blend-screen"
      />
      {/* tracking border shine — rendered as a thin outer ring via mask */}
      <div
        ref={borderRef}
        aria-hidden
        className="pointer-events-none absolute -inset-px rounded-[inherit] opacity-0 transition-opacity duration-300"
        style={{
          WebkitMask:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: "1px",
        }}
      />
    </div>
  );
}
